/**
 * Risk-Based CAPTCHA System - Constants
 *
 * Configuration constants for hCaptcha integration.
 * Centralized settings for timeouts, URLs, and error messages.
 *
 * @author AmalQ Frontend Team
 * @date 30-12-2025
 */

import type { CaptchaErrorCode } from './types';

/**
 * hCaptcha script URL
 * Official CDN URL for hCaptcha script
 */
export const HCAPTCHA_SCRIPT_URL = 'https://js.hcaptcha.com/1/api.js';

/**
 * Default timeout for invisible challenge (30 seconds)
 * If hCaptcha doesn't respond within this time, consider it failed
 */
export const DEFAULT_CHALLENGE_TIMEOUT = 30000;

/**
 * Token expiration time (2 minutes)
 * hCaptcha tokens expire after ~2 minutes
 * We use a conservative estimate to avoid edge cases
 */
export const TOKEN_EXPIRATION_MS = 120000;

/**
 * Script load timeout (15 seconds)
 * Max time to wait for hCaptcha script to load
 */
export const SCRIPT_LOAD_TIMEOUT = 15000;

/**
 * Maximum CAPTCHA retry attempts
 * After this many failed CAPTCHA attempts, give up
 */
export const MAX_CAPTCHA_RETRIES = 3;

/**
 * HTTP status codes that indicate CAPTCHA is required
 */
export const CAPTCHA_STATUS_CODES = {
  /** Medium risk - invisible CAPTCHA required */
  INVISIBLE: 202,
  /** High risk - visible CAPTCHA required (also used for validation errors, check body) */
  VISIBLE: 400,
} as const;

/**
 * Environment variable for bypassing CAPTCHA in tests
 * Set VITE_CAPTCHA_BYPASS=true in .env.development or .env.test
 */
export const CAPTCHA_BYPASS_ENV_KEY = 'VITE_CAPTCHA_BYPASS';

/**
 * Check if CAPTCHA bypass is enabled
 */
export const isCaptchaBypassEnabled = (): boolean => {
  return import.meta.env[CAPTCHA_BYPASS_ENV_KEY] === 'true';
};

/**
 * User-friendly error messages for CAPTCHA errors
 */
export const CAPTCHA_ERROR_MESSAGES: Record<CaptchaErrorCode, string> = {
  LOAD_FAILED: 'Failed to load verification system. Please refresh the page and try again.',
  EXECUTION_FAILED: 'Verification failed. Please try again.',
  NETWORK_ERROR: 'Network error during verification. Please check your connection and try again.',
  TIMEOUT: 'Verification timed out. Please try again.',
  TOKEN_EXPIRED: 'Verification expired. Please try again.',
  CHALLENGE_CLOSED: 'Verification cancelled. Please complete the security challenge to continue.',
  INVALID_SITE_KEY: 'Verification configuration error. Please try again later.',
  RATE_LIMITED: 'Too many verification attempts. Please wait a moment before trying again.',
  UNKNOWN: 'An unexpected error occurred during verification. Please try again.',
};

/**
 * Recoverable error codes (user can retry)
 */
export const RECOVERABLE_ERROR_CODES: CaptchaErrorCode[] = [
  'TIMEOUT',
  'TOKEN_EXPIRED',
  'CHALLENGE_CLOSED',
  'NETWORK_ERROR',
  'EXECUTION_FAILED',
];

/**
 * Check if an error code is recoverable
 */
export const isRecoverableError = (code: CaptchaErrorCode): boolean => {
  return RECOVERABLE_ERROR_CODES.includes(code);
};

/**
 * CSS z-index values for CAPTCHA components
 * Ensures CAPTCHA modal appears above other content
 */
export const CAPTCHA_Z_INDEX = {
  /** Verifying indicator (subtle, non-blocking) */
  INDICATOR: 9998,
  /** CAPTCHA modal overlay */
  OVERLAY: 9999,
  /** CAPTCHA widget itself */
  WIDGET: 10000,
} as const;

/**
 * Animation durations (milliseconds)
 */
export const CAPTCHA_ANIMATION_DURATION = {
  /** Fade in/out duration */
  FADE: 200,
  /** Scale animation duration */
  SCALE: 300,
} as const;

/**
 * Verifying indicator position
 */
export const VERIFYING_INDICATOR_POSITION = {
  /** Position from bottom edge */
  bottom: '24px',
  /** Position from right edge */
  right: '24px',
} as const;
